import { Fragment } from 'react';
import { Helmet } from 'react-helmet';
import {
  ProjectBackground,
  ProjectContainer,
  ProjectHeader,
  ProjectSection,
  ProjectSectionContent,
  ProjectSectionHeading,
  ProjectTextRow,
} from 'components/ProjectLayout';
import Link from 'components/Link';
import Footer from 'components/Footer';
import { useScrollRestore } from 'hooks';
import prerender from 'utils/prerender';
import usesBackground from 'assets/video/uses-background.mp4';
import usesBackgroundPlaceholder from 'assets/images/uses-background-placeholder.jpg';
import './index.css';

const Uses = () => {
  useScrollRestore();

  return (
    <Fragment>
      <Helmet>
        <title>More about me</title>
        <meta
          name="description"
          content="A list of hardware and software I use to do my thing"
        />
      </Helmet>
      <ProjectContainer className="uses">
        <ProjectBackground
          src={usesBackground}
          placeholder={usesBackgroundPlaceholder}
          opacity={0.7}
          entered={!prerender}
        />
        <ProjectHeader
          title="Uses"
          description="A somewhat comprehensive list of tools, apps, hardware, and more that I use on a daily basis to design and code things."
        />
        <ProjectSection first className="uses__section">
          <ProjectSectionContent>
            <ProjectTextRow width="m">
              <ProjectSectionHeading>Design</ProjectSectionHeading>
              <ul>
                <li>
                  <Link href="https://figma.com">Figma</Link> is my primary tool for UI
                  design these days. Made the switch from InVision in 2019 and haven't
                  looked back.
                </li>
                <li>
                  Any motion graphics I create are created in Adobe After Effects. So far
                  I haven't found a non-Adobe product that's as good. If anyone has
                  suggestions please <Link href="/contact">message me</Link>.
                </li>
                <li>
                  For any 3D models I use <Link href="https://blender.org">Blender</Link>.
                  Since 2.8 it's become way simpler to use and in a lot of ways better
                  than expensive paid tools like 3DS Max or Maya.
                </li>
              </ul>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection first className="uses__section">
          <ProjectSectionContent>
            <ProjectTextRow width="m">
              <ProjectSectionHeading>Development</ProjectSectionHeading>
              <ul>
                <li>
                  I use several IDEs for development, depending on the project and
                  platform.{' '}
                  <Link href="https://www.jetbrains.com/idea/">IntelliJ IDEA</Link> for
                  Java applications and front-end development.
                </li>
                <li>
                  <Link href="https://developer.android.com/studio">Android Studio</Link>{' '}
                  for developing Android apps.
                </li>
                <li>
                  <Link href="https://reactjs.org">React</Link> is my front end Javascript
                  library of choice. The component-centric mental model is the first thing
                  that truly made sense to me as a developer.
                </li>
                <li>
                  For 3D effects and image shaders I use{' '}
                  <Link href="https://threejs.org">three.js</Link>. It has a bit of a
                  learning curve but you can do some really powerful stuff with it.
                </li>
                <li>
                  I use {` `}
                  <Link href="https://www.jetbrains.com/rider/">Rider</Link>
                  {` `} and {` `}
                  <Link href="https://visualstudio.microsoft.com/vs/">
                    Visual Studio 2019
                  </Link>
                  {` `}
                  for .NET programming.
                </li>
                <li>
                  Depending on the project I use{' '}
                  <Link href="https://www.jetbrains.com/datagrip/">Datagrip</Link> or{' '}
                  <Link href="https://docs.microsoft.com/en-us/sql/ssms/sql-server-management-studio-ssms?view=sql-server-ver15">
                    SQL Server Management Studio
                  </Link>{' '}
                  for database implementation.
                </li>
                <li>
                  For embedded programming and testing I use{' '}
                  <Link href="https://www.jetbrains.com/clion/">CLion</Link> and{' '}
                  <Link href="https://microchipdeveloper.com/atstudio:studio7intro">
                    Atmel Studio 7
                  </Link>
                  .
                </li>
                <li>
                  For Javascript animations I use{' '}
                  <Link href="https://popmotion.io/api">Popmotion Pure 8</Link>, it's a
                  great way to add spring animations to three.js. All other animations are
                  CSS with{' '}
                  <Link href="https://reactcommunity.org/react-transition-group">
                    React Transition Group
                  </Link>{' '}
                  for enter/exit transitions.
                </li>
                <li>
                  For building and testing UI components in isolation I use{' '}
                  <Link href="https://storybook.js.org">Storybook</Link>.
                </li>
              </ul>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection first className="uses__section">
          <ProjectSectionContent>
            <ProjectTextRow width="m">
              <ProjectSectionHeading>
                Music production & Sound design
              </ProjectSectionHeading>
              <ul>
                <li>
                  <Link href="https://www.image-line.com">FL Studio</Link> is my primary
                  digital audio workstation. I am simply in love with the user interface
                  and how intuitive and easy it is to use. I also have a large amount of
                  plug-ins which would take days to list here.
                </li>
                <li>
                  <Link href="http://www.vintagesynth.com/korg/emx1.php">
                    Korg ElecTribe MX (EMX-1)
                  </Link>{' '}
                  is one of the hardware machines that I currently own as well as the{' '}
                  <Link href="http://www.vintagesynth.com/waldorf/blofeld.php">
                    Waldorf Blofeld
                  </Link>
                  .
                </li>
              </ul>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection className="uses__section">
          <ProjectSectionContent>
            <ProjectTextRow width="m">
              <ProjectSectionHeading>Hardware</ProjectSectionHeading>
              <ul>
                <li>
                  <Link href="https://rog.asus.com/dk/Laptops/ROG-Zephyrus/ROG-Zephyrus-M-GM501-Series/">
                    Asus ROG Zephyrus M GM501
                  </Link>{' '}
                  is my workhorse which I can carry anywhere. It has 8th gen i7 running at
                  3.9 GHz, 16 GB of RAM and a GTX 1070.
                </li>
                <li>
                  I use <Link href="https://www.krkmusic.com">KRK Rokit 5 G3</Link> as
                  studio monitors.
                </li>
              </ul>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
      </ProjectContainer>
      <Footer />
    </Fragment>
  );
};

export default Uses;
